import React from 'react';
import { Icon } from '@iconify/react';
import { AppButton } from '../ui';
import { DepotStatusBadge, DepotTypeBadge } from './DepotBadges';
import {
  depotAwaitingValidation,
  depotCanRequestBebedcLater,
  formatDisplayDate,
  getDestinationLabel,
  getTypeRdvLabel,
} from './depotUtils';

function DepotDocLinks({
  depot,
  bebedcPending = false,
  onBebedc,
  bebedcId,
}) {
  const showBebedcLink = Boolean(depot.bebedc_url) || bebedcPending;
  const canRequestBebedc = depotCanRequestBebedcLater(depot) && !bebedcPending;
  if (!depot.doc_url && !showBebedcLink && !canRequestBebedc && !depot.contexte_url && !depot.fichier_url) {
    return null;
  }

  const docLabel = depot.type_depot === 'document'
    ? 'Document'
    : depot.type_depot === 'notes'
      ? 'Notes'
      : 'Compte rendu';

  return (
    <div className="depot-doc-links">
      {depot.doc_url ? (
        <AppButton
          as="a"
          href={depot.doc_url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          variant="outline-primary"
          icon="mdi:open-in-new"
          title={depot.doc_nom || docLabel}
        >
          {docLabel}
        </AppButton>
      ) : null}
      {depot.bebedc_url ? (
        <AppButton
          as="a"
          href={depot.bebedc_url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          className="btn-bebedc"
          icon="mdi:open-in-new"
          title={depot.bebedc_nom || 'Analyse BEBEDC'}
        >
          Analyse BEBEDC
        </AppButton>
      ) : null}
      {bebedcPending && !depot.bebedc_url ? (
        <AppButton size="sm" className="btn-bebedc" disabled>
          Analyse en cours…
        </AppButton>
      ) : null}
      {canRequestBebedc ? (
        <AppButton
          size="sm"
          className="btn-bebedc"
          disabled={bebedcId === depot.id}
          title="Lancer l’analyse BEBEDC à partir du compte rendu"
          onClick={() => onBebedc(depot.id)}
        >
          {bebedcId === depot.id ? 'Analyse…' : 'Analyse BEBEDC'}
        </AppButton>
      ) : null}
      {depot.contexte_url ? (
        <AppButton
          as="a"
          href={depot.contexte_url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          variant="outline-primary"
          icon="mdi:open-in-new"
          title={depot.contexte_nom || 'Contexte client'}
        >
          Fiche client
        </AppButton>
      ) : null}
      {depot.fichier_url ? (
        <AppButton
          as="a"
          href={depot.fichier_url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          variant="outline-secondary"
          icon="mdi:open-in-new"
        >
          Fichier d’origine
        </AppButton>
      ) : null}
    </div>
  );
}

function DepotStatusActions({
  depot,
  onRelancer,
  onValider,
  onBebedc,
  onDelete,
  relancingId,
  validatingId,
  bebedcId,
  deletingId,
  uploading,
}) {
  if (depotAwaitingValidation(depot)) {
    return (
      <div className="depot-status-block">
        <div className="d-flex flex-wrap gap-2">
          <AppButton
            size="sm"
            disabled={validatingId === depot.id || bebedcId === depot.id}
            onClick={() => onValider(depot.id)}
          >
            {validatingId === depot.id ? 'Envoi…' : 'Confirmer'}
          </AppButton>
          <AppButton
            size="sm"
            variant="outline-secondary"
            disabled={deletingId === depot.id || uploading || validatingId === depot.id || bebedcId === depot.id}
            onClick={() => onDelete(depot)}
          >
            Annuler
          </AppButton>
          <AppButton
            size="sm"
            className="btn-bebedc"
            disabled={validatingId === depot.id || bebedcId === depot.id || !depot.doc_url}
            title={!depot.doc_url ? 'Le compte rendu Google Doc est requis' : 'Confirmer et lancer l’analyse BEBEDC'}
            onClick={() => onBebedc(depot.id)}
          >
            {bebedcId === depot.id ? 'Analyse…' : 'Analyse BEBEDC'}
          </AppButton>
        </div>
      </div>
    );
  }

  if (depot.statut === 'termine' && (depot.doc_url || depot.bebedc_url || depot.fichier_url)) {
    return null;
  }

  if (depot.statut === 'erreur') {
    return (
      <div className="depot-status-block">
        {depot.message_erreur && (
          <p className="depot-status-hint depot-status-hint--danger mb-0">{depot.message_erreur}</p>
        )}
        <AppButton
          size="sm"
          variant="outline-primary"
          className="align-self-start mt-8"
          disabled={relancingId === depot.id}
          onClick={() => onRelancer(depot.id)}
        >
          {relancingId === depot.id ? 'Relance…' : 'Réessayer'}
        </AppButton>
      </div>
    );
  }

  return null;
}

function DepotListCard({
  depot,
  destinations,
  onRelancer,
  onValider,
  onBebedc,
  onDelete,
  relancingId,
  validatingId,
  bebedcId,
  deletingId,
  uploading,
  bebedcPending,
}) {
  const detail = depot.type_depot === 'document' || depot.type_depot === 'notes'
    ? depot.objet || (depot.type_depot === 'notes' ? 'Notes' : 'Document')
    : getTypeRdvLabel(depot.type_rdv);
  const secondary = depot.type_depot === 'document' || depot.type_depot === 'notes'
    ? getDestinationLabel(depot.destination_dossier, destinations)
    : null;
  const hasActions = depotAwaitingValidation(depot)
    || depot.statut === 'erreur';

  return (
    <article className="depot-list-card">
      <div className="depot-list-card__top">
        <div className="depot-list-card__meta">
          <DepotTypeBadge typeDepot={depot.type_depot} />
          <span className="depot-list-card__date">{formatDisplayDate(depot.created_at)}</span>
          <DepotStatusBadge depot={depot} />
        </div>
        <span className="depot-icon-tip depot-icon-tip--end" data-tooltip="Retirer">
          <AppButton
            size="sm"
            variant="outline-secondary"
            icon="mdi:close"
            iconWidth={18}
            className="depot-icon-btn depot-icon-btn--danger depot-list-card__delete"
            disabled={deletingId === depot.id || uploading}
            aria-label="Retirer"
            onClick={() => onDelete(depot)}
          />
        </span>
      </div>

      <p className="depot-list-card__title mb-0">{depot.client_nom}</p>
      <p className="depot-list-card__detail mb-0">
        <span>{detail}</span>
        {depot.type_depot === 'audio_rdv' && depot.date_rdv ? (
          <span className="depot-list-card__secondary">
            <span className="depot-sep" aria-hidden> · </span>
            RDV du {formatDisplayDate(depot.date_rdv)}
          </span>
        ) : null}
        {secondary ? (
          <span className="depot-list-card__secondary">
            <span className="depot-sep" aria-hidden> · </span>
            {secondary}
          </span>
        ) : null}
        {depot.commercial_nom ? (
          <span className="depot-list-card__author">
            <span className="depot-sep" aria-hidden> · </span>
            {depot.commercial_nom}
          </span>
        ) : null}
        {depot.depose_par && depot.depose_par !== depot.commercial_nom ? (
          <span className="depot-list-card__author">
            <span className="depot-sep" aria-hidden> · </span>
            déposé par {depot.depose_par}
          </span>
        ) : null}
      </p>
      {depot.fichier_depose || depot.nom_fichier_original ? (
        <p className="depot-list-card__file mb-0" title={depot.fichier_depose || depot.nom_fichier_original}>
          <Icon icon="mdi:file-outline" width={14} aria-hidden />
          <span>{depot.fichier_depose || depot.nom_fichier_original}</span>
        </p>
      ) : null}
      <DepotDocLinks
        depot={depot}
        bebedcPending={bebedcPending}
        onBebedc={onBebedc}
        bebedcId={bebedcId}
      />
      {depot.type_depot === 'notes' && depot.texte_apercu ? (
        <p className="depot-list-card__preview mb-0">{depot.texte_apercu}</p>
      ) : null}

      {hasActions && (
        <div className="depot-list-card__status">
          <DepotStatusActions
            depot={depot}
            onRelancer={onRelancer}
            onValider={onValider}
            onBebedc={onBebedc}
            onDelete={onDelete}
            relancingId={relancingId}
            validatingId={validatingId}
            bebedcId={bebedcId}
            deletingId={deletingId}
            uploading={uploading}
          />
        </div>
      )}
    </article>
  );
}

export default DepotListCard;
